import { AfterContentInit, Component, OnInit, ViewChild } from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { MatSidenav, MatSidenavContent } from "@angular/material/sidenav";
import { Title } from "@angular/platform-browser";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { IdObject } from "@app/_classes/cosmos-entity";
import { TeamUtils } from "@app/_directives/teams-externa-link.directive";
import parseSubscriptionAsStatus from "@app/_helpers/parseSubscriptionAsStatus";
import { canViewDashboard, hasUserRoleControlling, hasUserRoleManager, usePermission } from "@app/_helpers/permission";
import { fromRxValue, hasPermission } from "@app/_helpers/utils";
import { FadeInOut, SlideUpDown, SlideUpDownBump } from "@app/animations/fade";
import { routerTransition, routerTransitionLeft } from "@app/animations/route";
import { AppService } from "@app/app.service";
import RouteGroup from "@app/classes/routegroup";
import { I18nService } from "@app/core";
import { LoaderComponent } from "@app/shared";
import { WorkspaceDialogComponent } from "@app/shared/dialogs/workspace-dialog/workspace-dialog.component";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { environment } from "@env/environment";
import { initFreshChatJs, isFreshChatInitialized } from "@env/freshchat";
import { IsBetaWindow } from "@env/version";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import {
	Logger,
	MyTimesQuery,
	UserService,
	UserSettingsQuery,
	Workspace,
	WorkspacesQuery,
	WorkspacesService,
} from "@timeghost/web-api";
import { differenceInDays } from "date-fns/esm/fp";
import { flow } from "lodash-es";
import { MediaObserver } from "ngx-flexible-layout";
import { combineLatest } from "rxjs";
import { distinctUntilChanged, filter, finalize, map, startWith, switchMap, tap } from "rxjs/operators";

const log = new Logger("Shell");
@UntilDestroy()
@Component({
	selector: "app-shell",
	templateUrl: "./shell.component.html",
	styleUrls: ["./shell.component.scss"],
	host: {
		class: "shell-host",
	},
	animations: [routerTransition, routerTransitionLeft, FadeInOut, SlideUpDown, SlideUpDownBump],
})
export class ShellComponent implements OnInit, AfterContentInit {
	isLoading: boolean;
	@ViewChild("routerLoader", { static: true })
	routerLoader: LoaderComponent;
	@ViewChild("sidenav", { static: true })
	sidenav: MatSidenav;
	@ViewChild("container", { static: true })
	private _shellContainer: MatSidenavContent;
	readonly workspace$manageProjects = this.userSettingsQuery.select().pipe(
		map((x) => {
			return !!x.workspace?.permissionSettings.groupsCanManageProjects.find((y) => hasPermission(y.id, x));
		})
	);
	readonly workspace$manageHourlyRates = this.userSettingsQuery.select().pipe(
		map((x) => {
			return !!x.workspace?.permissionSettings.groupsCanSeeBillableRates.find((y) => hasPermission(y.id, x));
		})
	);
	readonly workspace$canSeeProfitability = this.userSettingsQuery.select().pipe(
		map((x) => {
			return (
				!!x.workspace?.settings.profitabilityEnabled &&
				!!x.workspace?.permissionSettings.groupsCanSeeProfitability.find((y: { id: string }) =>
					hasPermission(y.id, x)
				)
			);
		})
	);
	readonly workspace$manageClients = this.userSettingsQuery.select().pipe(
		map((x) => {
			return !!x.workspace?.permissionSettings.groupsCanManageClients.find((y) => hasPermission(y.id, x));
		})
	);
	readonly workspace$manageTags = this.userSettingsQuery.select().pipe(
		map((x) => {
			return !!x.workspace?.permissionSettings.groupsCanManageTags.find((y) => hasPermission(y.id, x));
		})
	);
	readonly workspace$admin = this.userSettingsQuery.select().pipe(
		map((x) => {
			return !!x.workspace?.users.find((y) => y.admin && y.id === x.id);
		})
	);
	readonly workspaceSettingsNavActive$ = this.router.events.pipe(
		filter((e): e is NavigationEnd => e instanceof NavigationEnd),
		startWith(null),
		map(() => {
			const path = this.router.url.split("?")[0].split("#")[0];
			if (!path.startsWith("/settings/workspace")) return false;
			if (path.startsWith("/settings/workspace/team")) return false;
			if (path.startsWith("/settings/workspace/subscriptions")) return false;
			return true;
		}),
		distinctUntilChanged()
	);
	readonly isWorkspace$Manager = this.userSettingsQuery.select().pipe(
		map((x) => {
			return hasUserRoleManager(x);
		})
	);
	readonly hasRoleControlling$ = this.userSettingsQuery.select().pipe(
		map((x) => {
			return hasUserRoleControlling(x);
		})
	);
	readonly isSupervisor$ = fromRxValue(() =>
		this.userSettingsQuery.select().pipe(
			map((user) => {
				if (!user || user.workspace.settings.workinghoursEnabled === false) return false;
				// if (isAdmin(user)) return true;
				return !!user.workspace.users?.find((u) => u?.["supervisors"]?.find((s: IdObject) => s.id === user.id));
			})
		)
	);
	readonly dashboardViewable$ = fromRxValue(() =>
		this.userSettingsQuery.select().pipe(
			map((user) => {
				return canViewDashboard(user);
			}),
			tap((dashboardViewable) => log.debug({ dashboardViewable }))
		)
	);

	readonly supervisorViewable$ = fromRxValue(() =>
		this.userSettingsQuery
			.select()
			.pipe(map((d) => d.workspace?.settings?.supervisorViews ?? {}))
			.pipe(
				map((settings) => {
					if (settings.activity) {
						return "/supervise";
					} else if (settings.workinghours) {
						return "/supervise/workinghours";
					} else if (settings.rulebreaks) {
						return "/supervise/rulebreaks";
					}
					return null;
				})
			)
	);
	readonly canSeeProjectsInNavigation$ = this.userSettingsQuery.select().pipe(
		map((x) => {
			return !!x.workspace?.permissionSettings.groupsCanSeeProjectsInNavigation.find((y: any) =>
				hasPermission(y.id, x)
			);
		})
	);

	readonly permissions$ = usePermission();
	readonly managementSectionStorageKey = "shell_nav_management_expanded";
	readonly insightsSectionStorageKey = "shell_nav_insights_expanded";
	readonly adminSectionStorageKey = "shell_nav_admin_expanded";
	managementSectionExpanded = localStorage.getItem(this.managementSectionStorageKey) !== "false";
	insightsSectionExpanded = localStorage.getItem(this.insightsSectionStorageKey) !== "false";
	adminSectionExpanded = localStorage.getItem(this.adminSectionStorageKey) !== "false";

	toggleManagementSection(): void {
		this.managementSectionExpanded = !this.managementSectionExpanded;
		localStorage.setItem(this.managementSectionStorageKey, String(this.managementSectionExpanded));
	}

	toggleInsightsSection(): void {
		this.insightsSectionExpanded = !this.insightsSectionExpanded;
		localStorage.setItem(this.insightsSectionStorageKey, String(this.insightsSectionExpanded));
	}

	toggleAdminSection(): void {
		this.adminSectionExpanded = !this.adminSectionExpanded;
		localStorage.setItem(this.adminSectionStorageKey, String(this.adminSectionExpanded));
	}
	readonly user$ = fromRxValue(() =>
		this.userSettingsQuery
			.select()
			.pipe(untilDestroyed(this))
			.pipe(
				map((user) => {
					return {
						...user,
						links: {},
					};
				})
			)
	);
	get production() {
		return environment.production;
	}
	get meta() {
		return environment.meta;
	}
	constructor(
		private router: Router,
		private titleService: Title,
		private i18nService: I18nService,
		private workspaceService: WorkspacesService,
		private userSettingsQuery: UserSettingsQuery,
		private media: MediaObserver,
		private dialog: MatDialog,
		private languageService: TranslateService,
		private appService: AppService,
		private userService: UserService,
		private myTimesQuery: MyTimesQuery,
		private recordToolbar: RecordToolbarService,
		private workspaceQuery: WorkspacesQuery
	) {}
	get currentUser() {
		return this.userSettingsQuery.getValue();
	}
	get ShellContainer() {
		return this._shellContainer.getElementRef();
	}
	get isTeams() {
		return this.appService.isTeams();
	}

	readonly view$ = this.appService.selectedTimerView$;
	toggleNav() {
		return this.sidenav.toggle();
	}
	prepareRoute(outlet: RouterOutlet) {
		return outlet && outlet.activatedRouteData && outlet.activatedRouteData.state;
	}
	isWorkspaceAdmin(ws: Workspace) {
		return ws.users.findIndex((x) => x.admin && x.id === this.currentUser.id) !== -1;
	}
	isWorkspaceSettingsActive(): boolean {
		const path = this.router.url.split(/[?#]/)[0];
		return (
			path === "/settings/workspace" ||
			path === "/settings/workspace/" ||
			path.startsWith("/settings/workspace/workinghours") ||
			path.startsWith("/settings/workspace/projecthours")
		);
	}
	ngAfterContentInit() {
		this.isLoading = false;
	}

	setLanguage(language: string) {
		this.i18nService.language = language;
	}

	get currentDate() {
		return new Date();
	}

	getState(outlet: any) {
		return outlet.activatedRouteData.title || outlet.activatedRouteData.state;
	}

	get languages(): string[] {
		return this.i18nService.supportedLanguages;
	}

	get isMobile(): boolean {
		// return false;
		return this.media.isActive("xs") || this.media.isActive("sm");
	}
	isMobile$ = this.media.asObservable().pipe(map((x) => x.findIndex((y) => ["sm", "xs"].includes(y.mqAlias)) !== -1));
	isMobileWidgets$ = this.media
		.asObservable()
		.pipe(map((x) => x.findIndex((y) => ["sm", "xs", "md"].includes(y.mqAlias)) !== -1));
	get width(): number {
		return this.sidenav._getWidth();
	}
	getTranslatedDescription(routeGroup: RouteGroup) {
		return [...(routeGroup.optionalSubRouteLabels || []), ...routeGroup.routes.map((x) => x.routeText)]
			.map((x) => this.languageService.instant(x))
			.join(", ");
	}
	readonly runningTime$ = this.recordToolbar.runningTime.asObservable();
	readonly workinghoursEnabled$ = this.appService.comegoEnabled$;

	readonly projecthoursEnabled$ = this.appService.projecthoursEnabled$;

	get userSettings() {
		return this.userSettingsQuery.getValue();
	}
	get workspace() {
		return this.userSettings.workspace;
	}

	readonly workspace$ = this.userSettingsQuery.select().pipe(
		distinctUntilChanged(),
		map((x) => {
			const permissions = Object.entries(x.workspace.permissionSettings).map(([key, p]) => [
				key,
				!!p.find((_p: any) => hasPermission(_p.id, x)),
			]);
			return {
				...x.workspace,
				me: parseSubscriptionAsStatus(x.workspace, x),
				hasPermission: (permission: { id: string }[]) => permission?.find((p) => hasPermission(p.id, x)),
				permissions: {
					...permissions.reduce((l, [key, permit]: [string, boolean]) => {
						if (l[key] === undefined) l[key] = permit;
						return l;
					}, {}),
				},
			};
		})
	);
	readonly isWorkspaceOwner$ = this.workspace$.pipe(
		map((x) => x.users.findIndex((y) => y.admin && y.id === this.userSettings.id) !== -1)
	);
	readonly workspaceStatus$ = fromRxValue(() =>
		this.userSettingsQuery.select().pipe(
			distinctUntilChanged(),
			filter((x) => !!x.workspace),
			map((x) => {
				// use x.workspace from the same store update so subscription changes (e.g. after license update) are reflected immediately
				return parseSubscriptionAsStatus(x.workspace, x);
			})
		)
	);
	readonly showUser$ = combineLatest([
		this.workspaceStatus$.asObservable(),
		this.media.asObservable().pipe(
			distinctUntilChanged(),
			map(() => !!this.media.isActive(["sm", "xs"]))
		),
	]).pipe(
		map(([ws, showMedia]) => {
			return ws.isTeams || showMedia;
		})
	);
	parseTrailLength(t: number) {
		return flow(differenceInDays(Date.now()))(t * 1000);
	}
	get isBeta() {
		return !environment.production || IsBetaWindow();
	}
	openWorkspaceDialog() {
		const ref = this.dialog.open(WorkspaceDialogComponent, {
			maxWidth: "calc(100% - 32px)",
			closeOnNavigation: true,
			data: {
				activeWorkspace: this.workspace,
				showLoading: true,
			},
		});
		ref.afterClosed()
			.pipe(
				filter((x) => !!x),
				switchMap(() => {
					// Dialog already reset+reinit stores — only navigate + reconnect (avoid double-reset race)
					return this.router
						.navigateByUrl("/")
						.then(() => {
							this.appService.initSignalReconnectOnFailed();
						})
						.catch((err) => {
							log.error(err);
							return this.router.navigateByUrl("/not-found");
						});
				}),
				finalize(
					() =>
						this.appService.checkLoading("fullAppLoading") &&
						this.appService.removeLoading("fullAppLoading")
				)
			)
			.subscribe();
	}
	openWorkspacePlan() {
		if (this.isTeams) {
			TeamUtils.clickLink(TeamUtils.parseUrl(this.router, undefined, ["/settings/workspace/subscriptions"]));
			return;
		}
		this.router.navigate(["/settings/workspace/subscriptions"]);
	}
	get appVersion() {
		return environment.version;
	}

	ngOnInit() {}
	teamsLogout() {
		this.userService.teamsLogout();
	}
	openLink(url: string) {
		TeamUtils.clickLink(url, "external");
	}
	get isTeamsMobile() {
		return this.isTeams && this.isMobile;
	}
	widgetLoading = false;
	openWidget() {
		if (!isFreshChatInitialized()) {
			this.widgetLoading = true;
			return initFreshChatJs(this.userSettingsQuery.getValue().id)
				.then((freshChat: any) => {
					const { officeProfile, workspace, id, email, settings } = this.userSettingsQuery.getValue();
					freshChat("identify", "ticketForm", {
						email,
						name: `${officeProfile.givenName}${officeProfile.surname ? " " + officeProfile.surname : ""}`,
					});

					freshChat("open");

					this.widgetLoading = false;
				})
				.catch(() => {
					this.widgetLoading = false;
				});
		}
		window.FreshworksWidget("open");
	}
}
